/**
 * Code Review Checklist Template Form
 * Code review documentation and checklist
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const CodeReviewForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    pr_title: '', pr_number: '', repository: '', branch: '', date: new Date().toISOString().split('T')[0],
    author: '', reviewer: '', review_status: 'Pending',
    checklist: [
      { category: 'Code Quality', item: 'Code follows project coding standards', checked: false, notes: '' },
      { category: 'Code Quality', item: 'No code duplication', checked: false, notes: '' },
      { category: 'Code Quality', item: 'Functions are small and single-purpose', checked: false, notes: '' },
      { category: 'Testing', item: 'Unit tests added for new code', checked: false, notes: '' },
      { category: 'Testing', item: 'All tests pass', checked: false, notes: '' },
      { category: 'Security', item: 'No hardcoded credentials', checked: false, notes: '' },
      { category: 'Security', item: 'Input validation implemented', checked: false, notes: '' },
      { category: 'Documentation', item: 'Code is properly commented', checked: false, notes: '' },
      { category: 'Documentation', item: 'README updated if needed', checked: false, notes: '' }
    ],
    comments: [{ line: '', file: '', comment: '', severity: 'Suggestion', resolved: false }],
    overall_feedback: '', approval_status: 'Pending',
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    pr_title: 'Add user authentication with OAuth2',
    pr_number: 'PR-1234',
    repository: 'acme/customer-portal',
    branch: 'feature/oauth-auth',
    date: new Date().toISOString().split('T')[0],
    author: 'John Smith',
    reviewer: 'Jane Doe',
    review_status: 'In Progress',
    checklist: [
      { category: 'Code Quality', item: 'Code follows project coding standards', checked: true, notes: 'Good adherence to ESLint rules' },
      { category: 'Code Quality', item: 'No code duplication', checked: true, notes: '' },
      { category: 'Code Quality', item: 'Functions are small and single-purpose', checked: true, notes: '' },
      { category: 'Code Quality', item: 'Variable and function names are descriptive', checked: true, notes: '' },
      { category: 'Code Quality', item: 'No unnecessary complexity', checked: false, notes: 'AuthService class could be simplified' },
      { category: 'Testing', item: 'Unit tests added for new code', checked: true, notes: '85% coverage' },
      { category: 'Testing', item: 'All tests pass', checked: true, notes: '' },
      { category: 'Testing', item: 'Edge cases covered', checked: false, notes: 'Missing tests for token expiry' },
      { category: 'Security', item: 'No hardcoded credentials', checked: true, notes: '' },
      { category: 'Security', item: 'Input validation implemented', checked: true, notes: '' },
      { category: 'Security', item: 'No SQL injection vulnerabilities', checked: true, notes: 'Using parameterized queries' },
      { category: 'Security', item: 'Sensitive data properly encrypted', checked: true, notes: '' },
      { category: 'Performance', item: 'No obvious performance issues', checked: true, notes: '' },
      { category: 'Performance', item: 'Database queries optimized', checked: true, notes: '' },
      { category: 'Documentation', item: 'Code is properly commented', checked: true, notes: '' },
      { category: 'Documentation', item: 'README updated if needed', checked: false, notes: 'Need to add OAuth setup instructions' },
      { category: 'Documentation', item: 'API documentation updated', checked: true, notes: '' }
    ],
    comments: [
      { line: '45', file: 'src/services/AuthService.ts', comment: 'Consider extracting token validation to separate function for reusability', severity: 'Suggestion', resolved: false },
      { line: '78', file: 'src/services/AuthService.ts', comment: 'This try-catch block should have specific error handling for different OAuth errors', severity: 'Required', resolved: false },
      { line: '23', file: 'src/controllers/AuthController.ts', comment: 'Add rate limiting to prevent brute force attacks', severity: 'Required', resolved: true },
      { line: '112', file: 'src/services/AuthService.ts', comment: 'Nice implementation of token refresh logic!', severity: 'Praise', resolved: false }
    ],
    overall_feedback: 'Good implementation of OAuth2 authentication. Main concerns are around error handling specificity and missing edge case tests. Once the required changes are addressed, this is ready for merge.',
    approval_status: 'Changes Requested',
    rai_assignments: [
      { task: 'Address review comments', responsible: 'John Smith', accountable: 'Tech Lead', informed: 'Jane Doe', due_date: '2025-01-18', status: 'In Progress' }
    ],
    followups: [
      { item: 'Add token expiry tests', owner: 'John Smith', due_date: '2025-01-17', priority: 'High', status: 'Pending' },
      { item: 'Update README with OAuth setup', owner: 'John Smith', due_date: '2025-01-17', priority: 'Medium', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Code Review Checklist Draft";
      const payload = {
        name: draftName,
        template_type: "Code Review Checklist",
        template_id: "code-review",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "code-review", "Code Review", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("code-review", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/code-review`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Code_Review_${formData.pr_number || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Code Review Checklist",
        formData,
        settings: activeSettings || {},
        templateType: "Code Review Checklist",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateChecklist = (index, field, value) => {
    const checklist = [...formData.checklist];
    checklist[index] = { ...checklist[index], [field]: value };
    setFormData({ ...formData, checklist });
  };

  const addComment = () => {
    setFormData({
      ...formData,
      comments: [...formData.comments, { line: '', file: '', comment: '', severity: 'Suggestion', resolved: false }]
    });
  };

  const updateComment = (index, field, value) => {
    const comments = [...formData.comments];
    comments[index] = { ...comments[index], [field]: value };
    setFormData({ ...formData, comments });
  };

  const removeComment = (index) => {
    setFormData({ ...formData, comments: formData.comments.filter((_, i) => i !== index) });
  };

  const reviewStatuses = ['Pending', 'In Progress', 'Completed'];
  const approvalStatuses = ['Pending', 'Approved', 'Changes Requested', 'Rejected'];
  const severities = ['Praise', 'Suggestion', 'Required', 'Critical'];

  const checkedCount = formData.checklist.filter(c => c.checked).length;
  const categories = [...new Set(formData.checklist.map(c => c.category))];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Code Review Checklist</h1><p className="text-sm text-gray-500">Code review documentation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Code Review</CardTitle>
            <div className="flex gap-4 mt-2">
              <div className="bg-blue-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Checklist:</span> <span className="font-bold text-blue-700">{checkedCount}/{formData.checklist.length}</span></div>
              <div className="bg-yellow-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Comments:</span> <span className="font-bold text-yellow-700">{formData.comments.length}</span></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>PR Title *</Label><Input value={formData.pr_title} onChange={(e) => setFormData({...formData, pr_title: e.target.value})} placeholder="Add user authentication" /></div>
              <div><Label>PR Number</Label><Input value={formData.pr_number} onChange={(e) => setFormData({...formData, pr_number: e.target.value})} placeholder="PR-1234" /></div>
              <div><Label>Repository</Label><Input value={formData.repository} onChange={(e) => setFormData({...formData, repository: e.target.value})} placeholder="org/repo" /></div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Author</Label><Input value={formData.author} onChange={(e) => setFormData({...formData, author: e.target.value})} placeholder="Author name" /></div>
              <div><Label>Reviewer</Label><Input value={formData.reviewer} onChange={(e) => setFormData({...formData, reviewer: e.target.value})} placeholder="Reviewer name" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Approval</Label>
                <Select value={formData.approval_status} onValueChange={(v) => setFormData({...formData, approval_status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{approvalStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Review Checklist</Label>
              {categories.map(category => (
                <div key={category} className="mt-4">
                  <Label className="text-sm font-medium text-gray-700">{category}</Label>
                  <div className="space-y-2 mt-2">
                    {formData.checklist.filter(c => c.category === category).map((item, idx) => {
                      const actualIdx = formData.checklist.findIndex(c => c === item);
                      return (
                        <div key={idx} className="flex items-start gap-3 p-2 bg-gray-50 rounded">
                          <Checkbox checked={item.checked} onCheckedChange={(checked) => updateChecklist(actualIdx, 'checked', checked)} className="mt-1" />
                          <div className="flex-1">
                            <span className={`text-sm ${item.checked ? 'text-green-700' : ''}`}>{item.item}</span>
                            <Input value={item.notes} onChange={(e) => updateChecklist(actualIdx, 'notes', e.target.value)} placeholder="Notes..." className="text-xs h-7 mt-1" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Review Comments</Label>
              <div className="space-y-3 mt-3">
                {formData.comments.map((comment, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-6 gap-2 bg-gray-50 p-3 rounded">
                    <div><Label className="text-xs">File</Label><Input value={comment.file} onChange={(e) => updateComment(idx, 'file', e.target.value)} placeholder="src/file.ts" className="text-sm" /></div>
                    <div><Label className="text-xs">Line</Label><Input value={comment.line} onChange={(e) => updateComment(idx, 'line', e.target.value)} placeholder="45" className="text-sm" /></div>
                    <div className="md:col-span-2"><Label className="text-xs">Comment</Label><Input value={comment.comment} onChange={(e) => updateComment(idx, 'comment', e.target.value)} placeholder="Review comment" className="text-sm" /></div>
                    <div><Label className="text-xs">Severity</Label>
                      <Select value={comment.severity} onValueChange={(v) => updateComment(idx, 'severity', v)}>
                        <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>{severities.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-1">
                      <div className="flex items-center gap-2 flex-1">
                        <Checkbox checked={comment.resolved} onCheckedChange={(checked) => updateComment(idx, 'resolved', checked)} />
                        <span className="text-xs">Resolved</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeComment(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addComment} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Comment</Button>
            </div>

            <div><Label>Overall Feedback</Label><Textarea value={formData.overall_feedback} onChange={(e) => setFormData({...formData, overall_feedback: e.target.value})} placeholder="Summary of the review..." rows={3} /></div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CodeReviewForm;
